// ── Loadout Slots ──
//
// Maps slot keys (1, 2, 3) to the weapon currently in the primary, secondary,
// and melee slots. Pre-2026-04-29 the slot keybinds were hardcoded to glock /
// usp / knife regardless of what the player owned, so pressing 1 after buying
// an AK-47 sent weapon_switch{glock} and reverted the rifle. The fix is a
// per-loadout slot table updated on buy / pickup / respawn / round_start /
// arms_race_advance.

import { WEAPONS, type WeaponId } from '../core/config';

export type WeaponSlot = 'primary' | 'secondary' | 'melee';

export interface LoadoutSlots {
  primary: WeaponId | null;
  secondary: WeaponId | null;
  melee: WeaponId;
}

export function weaponSlotFor(id: WeaponId): WeaponSlot {
  const t = WEAPONS[id]?.type;
  if (t === 'melee') return 'melee';
  if (t === 'pistol') return 'secondary';
  return 'primary';
}

/**
 * G-M-003: unknown team IDs default to CT (USP secondary) instead of a null
 * secondary. The previous `null` branch shipped a pistol-less player whenever
 * the team value was 0 (uninitialised), out-of-range, or transiently unset
 * mid-spawn. Spectator and free-fly flows use the spectator camera and never
 * construct a LocalPlayer loadout, so this branch is now strictly a safety
 * net for malformed `team` values; CT pistols are the conservative fallback
 * since CT is the canonical "starts holstered" side.
 */
export function defaultLoadoutForTeam(team: number): LoadoutSlots {
  return {
    primary: null,
    secondary: team === 1 ? 'glock' : 'usp',
    melee: 'knife',
  };
}

export function setLoadoutSlot(slots: LoadoutSlots, id: WeaponId): void {
  switch (weaponSlotFor(id)) {
    case 'melee':
      slots.melee = id;
      break;
    case 'secondary':
      slots.secondary = id;
      break;
    case 'primary':
      slots.primary = id;
      break;
  }
}

export function resetLoadoutForTeam(slots: LoadoutSlots, team: number): void {
  const def = defaultLoadoutForTeam(team);
  slots.primary = def.primary;
  slots.secondary = def.secondary;
  slots.melee = def.melee;
}

// nextWeaponInCycle returns the next weapon when cycling through the player's
// owned slots (primary → secondary → melee → primary). Mobile's single WPN
// button used a hardcoded ['glock','usp','knife'] cycle that ignored bought
// rifles, so once the player tapped past their rifle they could never reach
// it again. Empty slots are skipped; if `currentId` is not in the cycle (e.g.
// holding a freshly-bought rifle whose slot table hasn't been mirrored yet)
// the cycle starts at the first non-null slot.
export function nextWeaponInCycle(
  slots: LoadoutSlots,
  currentId: WeaponId | null,
): WeaponId | null {
  const cycle: WeaponId[] = [];
  if (slots.primary) cycle.push(slots.primary);
  if (slots.secondary) cycle.push(slots.secondary);
  cycle.push(slots.melee);

  if (cycle.length === 0) return null;
  const idx = currentId ? cycle.indexOf(currentId) : -1;
  return cycle[(idx + 1) % cycle.length];
}
